import { useEffect, useRef, useState } from 'react';
import { STREAM_API, getToken } from '../utils/streamApi';
import { HlsVideoPlayer } from '../pages/ManageRecordingsPage';

// ── Session clip ──────────────────────────────────────────────────────────────
// A report row's footage in a popup: the recording from a little before the
// session started to a little after it ended, played from the DVR (streamed, so
// nothing is cut or downloaded up front), plus one download — up to CLIP_SECS
// from wherever the player is. One file per click on purpose: a whole long
// session as many files at once trips the browser's multiple-download block,
// and the part worth sending someone is the part that was just watched.
//
// Times are the box's (session and recording names both are), so the camera's
// on-screen clock running a few minutes off does not matter here.

const PAD_SECS  = 30;
const CLIP_SECS = 5 * 60;

const pad   = n => String(n).padStart(2, '0');
const dtStr = ts => { const d = new Date(ts * 1000);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`; };
const clk   = ts => { const d = new Date(ts * 1000); return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; };

export default function SessionClipModal({ camIdx, camName, title, start, end, onClose }) {
  // Fixed when the popup opens: an in-progress session's end moves, and a range
  // that changed on every render would reload the player each time.
  const [{ from, to }] = useState(() => ({
    from: Math.floor(start - PAD_SECS),
    to:   Math.min(Math.floor(end + PAD_SECS), Math.floor(Date.now() / 1000)),
  }));
  const span = Math.max(1, to - from);

  const [token, setToken]   = useState(null);
  const [state, setState]   = useState('checking');   // checking | ok | gone | error
  const [pos, setPos]       = useState(0);            // seconds into the clip
  const [speed, setSpeed]   = useState(1);
  const [busy, setBusy]     = useState(false);
  const [dlErr, setDlErr]   = useState(null);
  const videoRef = useRef(null);

  useEffect(() => { getToken().then(setToken).catch(() => setState('error')); }, []);

  // Ask for the playlist first: a session older than the recording retention has
  // no footage left, and an empty player says nothing about why.
  useEffect(() => {
    if (!token) return;
    fetch(`${STREAM_API}/hls/${camIdx}/dvr.m3u8?from_dt=${dtStr(from)}&to_dt=${dtStr(to)}&token=${token}`)
      .then(r => setState(r.ok ? 'ok' : r.status === 404 ? 'gone' : 'error'))
      .catch(() => setState('error'));
  }, [token, camIdx, from, to]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const clipFrom = from + Math.floor(pos);
  const clipTo   = Math.min(clipFrom + CLIP_SECS, to);

  const seek = s => { const v = videoRef.current; if (v) v.currentTime = s; setPos(s); };

  const download = async () => {
    if (!token || clipTo <= clipFrom) return;
    setBusy(true); setDlErr(null);
    const url  = `${STREAM_API}/recordings/export?cam_idx=${camIdx}&from_dt=${dtStr(clipFrom)}&to_dt=${dtStr(clipTo)}&token=${token}`;
    const name = `${camName}_${title}_${dtStr(clipFrom)}_to_${dtStr(clipTo).slice(-6)}.mp4`.replace(/[^\w.-]+/g, '_');
    try {
      // Fetch the bytes rather than pointing the browser at the URL — inside the
      // iOS WebView a navigation to the file replaces the app (see the recordings page).
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status === 404 ? 'No recording for this part' : `Download failed (${res.status})`);
      const blob = await res.blob();
      const file = new File([blob], name, { type: 'video/mp4' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
      } else {
        const obj = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = obj; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(obj), 60_000);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') setDlErr(e?.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.8)',
      display: 'grid', placeItems: 'center', padding: 16 }}>
      {/* Same surface as the report's other popups (var(--ind-ink)), so the title and
          times keep their contrast; capped at the window so the download stays on screen. */}
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(960px, 100%)', background: 'var(--ind-ink)',
        borderRadius: 10, padding: 12, color: 'var(--ind-tx)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
        boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>
            🎥 {camName} · {title} · {clk(start)}–{clk(end)}
          </span>
          <button className="ind-pill" style={{ marginLeft: 'auto' }} onClick={onClose}>✕ Close</button>
        </div>

        {state === 'checking' && <div className="ind-empty" style={{ padding: 40, textAlign: 'center' }}>Loading recording…</div>}
        {state === 'gone' && (
          <div className="ind-empty" style={{ padding: 40, textAlign: 'center', lineHeight: 1.7 }}>
            The recording for this time is no longer on the box —<br />
            older recordings are removed automatically to free space.
          </div>)}
        {state === 'error' && <div className="ind-empty" style={{ padding: 40, textAlign: 'center' }}>Couldn’t load the recording. Try again.</div>}

        {state === 'ok' && token && (
          <>
            <div style={{ position: 'relative', background: '#000', borderRadius: 6, overflow: 'hidden' }}>
              <HlsVideoPlayer camIdx={camIdx} token={token} fromDt={dtStr(from)} toDt={dtStr(to)} offset={0}
                className="session-clip-video" speed={speed} onSpeed={setSpeed}
                onTimeUpdate={e => { videoRef.current = e.target; setPos(e.target.currentTime); }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontFamily: 'var(--ind-mono)', fontSize: 12, minWidth: 64 }}>{clk(from + pos)}</span>
              <input type="range" min={0} max={span} step={1} value={Math.min(pos, span)}
                onChange={e => seek(Number(e.target.value))} style={{ flex: 1 }} aria-label="Position" />
              <span style={{ fontFamily: 'var(--ind-mono)', fontSize: 12, minWidth: 64, textAlign: 'right' }}>{clk(to)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <button className="ind-pill on" onClick={download} disabled={busy || clipTo <= clipFrom}>
                {busy ? 'Preparing…' : `⬇ ${clk(clipFrom)} – ${clk(clipTo)}`}
              </button>
              <span style={{ fontSize: 11, color: 'var(--ind-tx3)', lineHeight: 1.5 }}>
                {span > CLIP_SECS
                  ? `Downloads up to 5 min from where the video is. This session is ${Math.round(span / 60)} min — move the video on and download again for the rest.`
                  : 'Downloads this session as one clip.'}
              </span>
              {dlErr && <span style={{ fontSize: 12, color: 'var(--ind-bad, #e74c3c)' }}>{dlErr}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
